import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Search, X, Loader2, Hash } from "lucide-react";
import { byLastName } from "@/lib/format";

function buildName(conv, values) {
  let out = conv.pattern || "";
  (conv.variables || []).forEach((v) => {
    let val = values[v.key] ?? "";
    if (v.kind === "number") {
      const digits = Math.max(1, parseInt(v.digits || 1, 10));
      const n = parseInt(val === "" ? (conv.counter || 1) : val, 10);
      val = isNaN(n) ? "" : String(n).padStart(digits, "0");
    }
    out = out.replaceAll(`{${v.key}}`, String(val).trim());
  });
  return out.trim();
}

function PeoplePicker({ users, selected, onChange, testid, label }) {
  const { t } = useLang();
  const [q, setQ] = useState("");
  const map = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);
  const list = useMemo(() => users.filter((u) => `${u.displayName} ${u.userPrincipalName}`.toLowerCase().includes(q.toLowerCase())).sort(byLastName).slice(0, 60), [users, q]);
  const toggle = (id) => onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  return (
    <div className="space-y-1.5" data-testid={testid}>
      <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{label} ({selected.length})</Label>
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {selected.map((id) => (
            <span key={id} className="inline-flex items-center gap-1 rounded-sm border border-primary/30 bg-primary/5 px-1.5 py-0.5 text-[11px] text-primary">
              {map[id]?.displayName || id}
              <button type="button" onClick={() => toggle(id)} data-testid={`${testid}-remove-${id}`}><X size={11} /></button>
            </span>
          ))}
        </div>
      )}
      <div className="relative">
        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("teams.searchPeople")} className="rounded-sm border-zinc-300 pl-8 text-sm" data-testid={`${testid}-search`} />
      </div>
      <div className="max-h-32 overflow-auto rounded-sm border border-zinc-200">
        {list.map((u) => (
          <label key={u.id} className="flex cursor-pointer items-center gap-2 border-b border-zinc-100 px-2 py-1.5 text-sm last:border-0 hover:bg-zinc-50" data-testid={`${testid}-opt-${u.id}`}>
            <input type="checkbox" checked={selected.includes(u.id)} onChange={() => toggle(u.id)} className="accent-primary" />
            <span className="truncate text-zinc-700">{u.displayName}</span>
            <span className="ml-auto truncate text-[11px] text-zinc-400">{u.userPrincipalName}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

export function TeamFormDialog({ open, onOpenChange, mode, team, users, conventions, onSaved }) {
  const { t } = useLang();
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [visibility, setVisibility] = useState("Private");
  const [firstChannel, setFirstChannel] = useState("General Discussion");
  const [owners, setOwners] = useState([]);
  const [members, setMembers] = useState([]);
  const [channels, setChannels] = useState([]); // edit: existing
  const [removeChannels, setRemoveChannels] = useState([]);
  const [newChannel, setNewChannel] = useState("");
  const enforced = conventions.find((c) => c.enforced);
  const [useConv, setUseConv] = useState(!!enforced);
  const [convId, setConvId] = useState((enforced || conventions[0])?.id || "");
  const [convValues, setConvValues] = useState({});
  const conv = conventions.find((c) => c.id === convId);

  useEffect(() => {
    if (!open) return;
    if (mode === "edit" && team) {
      setLoading(true);
      api.get(`/teams/${team.id}/detail`).then((r) => {
        const d = r.data;
        setName(d.displayName || ""); setDescription(d.description || "");
        setVisibility(d.visibility || "Private");
        setOwners(d.owners.map((o) => o.id)); setMembers(d.members.map((m) => m.id));
        setChannels(d.channels || []);
      }).catch((e) => toast.error(apiErr(e))).finally(() => setLoading(false));
      setRemoveChannels([]); setNewChannel("");
    } else {
      setName(""); setDescription(""); setVisibility("Private"); setFirstChannel("General Discussion");
      setOwners([]); setMembers([]); setChannels([]); setRemoveChannels([]); setNewChannel("");
      setUseConv(!!enforced); setConvId((enforced || conventions[0])?.id || ""); setConvValues({});
    }
  }, [open, mode, team]); // eslint-disable-line

  const previewName = useConv && conv ? buildName(conv, convValues) : name;

  const submit = async () => {
    if (owners.length < 2) { toast.error(t("teams.needOwners")); return; }
    if (mode === "create" && !firstChannel.trim()) { toast.error(t("teams.needChannel")); return; }
    if (!useConv && !name.trim()) { toast.error(t("teams.needName")); return; }
    setBusy(true);
    try {
      if (mode === "create") {
        const body = { description, visibility, firstChannel, owners, members,
          useConvention: useConv, conventionId: convId, conventionValues: convValues };
        if (!useConv) body.displayName = name.trim();
        await api.post("/teams", body);
        toast.success(t("teams.created"));
      } else {
        const adds = newChannel.split(",").map((s) => s.trim()).filter(Boolean);
        await api.patch(`/teams/${team.id}`, { displayName: name.trim(), description, visibility, owners, members,
          addChannels: adds, removeChannels });
        toast.success(t("teams.updated"));
      }
      onOpenChange(false);
      onSaved();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm sm:max-w-2xl" data-testid="team-form-dialog">
        <DialogHeader>
          <DialogTitle className="text-sm">{mode === "create" ? t("teams.createTitle") : t("teams.editTitle")}</DialogTitle>
          <DialogDescription className="text-xs">{mode === "create" ? t("teams.createDesc") : t("teams.editDesc")}</DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="animate-spin text-primary" /></div>
        ) : (
          <div className="max-h-[65vh] space-y-4 overflow-auto pr-1">
            {mode === "create" && conventions.length > 0 && (
              <div className="rounded-sm border border-zinc-200 p-3">
                <label className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-zinc-800">{t("teams.useConvention")}{enforced && <span className="ml-2 text-[10px] uppercase text-amber-600">{t("teams.enforced")}</span>}</span>
                  <Switch checked={useConv} disabled={!!enforced} onCheckedChange={setUseConv} data-testid="team-use-convention" />
                </label>
                {useConv && (
                  <div className="mt-3 space-y-2">
                    {conventions.length > 1 && (
                      <Select value={convId} onValueChange={setConvId}>
                        <SelectTrigger className="rounded-sm border-zinc-300 text-sm" data-testid="team-convention-select"><SelectValue /></SelectTrigger>
                        <SelectContent>{conventions.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}</SelectContent>
                      </Select>
                    )}
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                      {(conv?.variables || []).map((v) => (
                        <div key={v.key} className="space-y-1">
                          <Label className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">{v.key}</Label>
                          {v.kind === "list" ? (
                            <Select value={convValues[v.key] || ""} onValueChange={(val) => setConvValues({ ...convValues, [v.key]: val })}>
                              <SelectTrigger className="rounded-sm border-zinc-300 text-sm" data-testid={`conv-var-${v.key}`}><SelectValue placeholder="—" /></SelectTrigger>
                              <SelectContent>{(v.values || []).map((opt) => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}</SelectContent>
                            </Select>
                          ) : v.kind === "number" ? (
                            <Input value={convValues[v.key] ?? ""} onChange={(e) => setConvValues({ ...convValues, [v.key]: e.target.value })} placeholder={`auto (${String(conv.counter || 1).padStart(v.digits || 1, "0")})`} data-testid={`conv-var-${v.key}`} className="rounded-sm border-zinc-300 text-sm" />
                          ) : (
                            <Input value={convValues[v.key] || ""} onChange={(e) => setConvValues({ ...convValues, [v.key]: e.target.value })} data-testid={`conv-var-${v.key}`} className="rounded-sm border-zinc-300 text-sm" />
                          )}
                        </div>
                      ))}
                    </div>
                    <div className="rounded-sm bg-zinc-100 px-3 py-2 font-mono text-sm text-zinc-700" data-testid="team-name-preview">{previewName || "—"}</div>
                  </div>
                )}
              </div>
            )}
            {(!useConv || mode === "edit") && (
              <div className="space-y-1.5">
                <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("teams.fieldName")}</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="team-name" className="rounded-sm border-zinc-300" />
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("teams.fieldDescription")}</Label>
              <Input value={description} onChange={(e) => setDescription(e.target.value)} data-testid="team-description" className="rounded-sm border-zinc-300" />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("teams.fieldVisibility")}</Label>
                <Select value={visibility} onValueChange={setVisibility}>
                  <SelectTrigger className="rounded-sm border-zinc-300" data-testid="team-visibility"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Private">{t("teams.private")}</SelectItem>
                    <SelectItem value="Public">{t("teams.public")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {mode === "create" && (
                <div className="space-y-1.5">
                  <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("teams.fieldChannel")}</Label>
                  <Input value={firstChannel} onChange={(e) => setFirstChannel(e.target.value)} data-testid="team-first-channel" className="rounded-sm border-zinc-300" />
                </div>
              )}
            </div>
            <PeoplePicker users={users} selected={owners} onChange={setOwners} testid="team-owners" label={t("teams.owners")} />
            <PeoplePicker users={users} selected={members} onChange={setMembers} testid="team-members" label={t("teams.members")} />
            {mode === "edit" && (
              <div className="space-y-1.5">
                <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("teams.channels")}</Label>
                <div className="flex flex-wrap gap-1">
                  {channels.map((c) => {
                    const rm = removeChannels.includes(c.id);
                    const isGeneral = (c.displayName || "").toLowerCase() === "general";
                    return (
                      <span key={c.id} className={`inline-flex items-center gap-1 rounded-sm border px-1.5 py-0.5 text-[11px] ${rm ? "border-red-200 bg-red-50 text-red-500 line-through" : "border-zinc-200 text-zinc-600"}`}>
                        <Hash size={10} />{c.displayName}
                        {!isGeneral && <button type="button" onClick={() => setRemoveChannels(rm ? removeChannels.filter((x) => x !== c.id) : [...removeChannels, c.id])} data-testid={`team-channel-rm-${c.id}`}><X size={11} /></button>}
                      </span>
                    );
                  })}
                </div>
                <Input value={newChannel} onChange={(e) => setNewChannel(e.target.value)} placeholder={t("teams.addChannelsPh")} data-testid="team-add-channels" className="rounded-sm border-zinc-300 text-sm" />
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">{t("common.cancel")}</Button>
          <Button onClick={submit} disabled={busy || loading} data-testid="team-form-submit" className="rounded-sm bg-primary hover:bg-[#002480]">
            {busy ? <Loader2 size={15} className="mr-2 animate-spin" /> : null}{mode === "create" ? t("teams.create") : t("teams.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
